import { useFlags } from 'launchdarkly-react-client-sdk';

export const allowedBackButtonRoutes = [
  'participants',
  'groups',
  'educatorArea',
  'search',
  'myspacenav',
  'materials',
  'announcements',
  'common-room',
  'messages',
  'groupSets',
  'home',
];

const FROM = Object.freeze({
  MATERIALS: 'materials',
  SEARCH: 'search',
  EDUCATOR_AREA: 'educatorArea',
  COMMON_ROOM: 'common-room',
  ANNOUNCEMENTS: 'announcements',
  MYSPACENAV: 'myspacenav',
  MESSAGES: 'messages',
  GROUP_SETS: 'groupSets',
  HOME: 'home',
});

export const showBackButtonForContent = () => {
  const hash = window.location.hash || '';

  // Extract query params from hash part
  const queryIndex = hash.indexOf('?');
  if (queryIndex === -1) {
    return { showBackbutton: false, queryParams: {} };
  }

  const queryString = hash.substring(queryIndex + 1);
  const params = new URLSearchParams(queryString);

  const from = params.get('from');

  // Convert URLSearchParams → plain object
  const queryParams = Object.fromEntries(params.entries());

  // Support comma-separated "from" values
  const fromList = from ? from.split(',') : [];
  // Check if all from values are included in allowedBackButtonRoutes
  const isValid = fromList.every((routes) =>
    allowedBackButtonRoutes.includes(routes)
  );

  if (isValid) {
    return {
      showBackbutton: true,
      queryParams,
    };
  }
  return { showBackbutton: false, queryParams: {} };
};

export const goToMySpaceNav = (spaceId) => {
  return `/dashboard/${spaceId}/myspacenav`;
};

const goToOmniSearch = () => {
  return `/search`;
};

const goToHome = () => {
  return `/home`;
};

export const goToMaterialsWithBackButton = (spaceId, sectionId, query) => {
  const fromParam = query?.from ?? 'myspacenav';

  if (sectionId) {
    return `/dashboard/${spaceId}/journey/materials/${sectionId}?from=${fromParam}`;
  }
  return `/dashboard/${spaceId}/journey/materials?from=${fromParam}`;
};

export const goToEducatorAreaWithBackButton = (spaceId, sectionId, query) => {
  if (sectionId) {
    return `/dashboard/${spaceId}/journey/educatorArea/${sectionId}?from=${
      query && query?.from ? query.from : 'myspacenav'
    }`;
  }
  return `/dashboard/${spaceId}/journey`;
};

const goToCommonRoomWithBackButton = (spaceId) => {
  return `/dashboard/${spaceId}/community/common-room?from=myspacenav`;
};

const goToAnnouncementsWithBackButton = (spaceId) => {
  return `/dashboard/${spaceId}/community/announcement?from=myspacenav`;
};

const goToMessagesWithBackButton = (spaceId, msgId = undefined) => {
  if (msgId) {
    return `/dashboard/${spaceId}/messages/${msgId}`;
  }
  return `/dashboard/${spaceId}/messages`;
};

export const goToParticipantsWithBackButton = (spaceId) => {
  return `/dashboard/${spaceId}/people/participants`;
};

// Function to get back button route
export const getBackButtonRoute = () => {
  if (typeof window === 'undefined') return null;

  const hash = window.location.hash.slice(1); // Remove the '#' character
  const [hashPath, QueryParams] = hash?.split('?');

  const hashParameters = hashPath?.split('/').filter(Boolean);
  const targetSpaceId = hashParameters[1]; // Assuming the spaceId is always the second segment

  // Fallback: If no targetSpaceId is present
  if (!targetSpaceId) return `/`;

  const fromChain = (new URLSearchParams(QueryParams).get('from') || '')
    .split(',')
    .filter(Boolean);
  const fromDestination = fromChain[fromChain.length - 1];
  const remainingFrom =
    fromChain.length > 1
      ? { from: fromChain.slice(0, -1).join(',') }
      : undefined;

  if (fromDestination === FROM.MATERIALS) {
    const returnSection = new URLSearchParams(QueryParams || '').get(
      'returnSection'
    );
    return goToMaterialsWithBackButton(
      targetSpaceId,
      returnSection || undefined,
      remainingFrom
    );
  }
  if (fromDestination === FROM.HOME) {
    return goToHome();
  }
  if (fromDestination === FROM.SEARCH) {
    return goToOmniSearch();
  }
  if (fromDestination === FROM.EDUCATOR_AREA) {
    return goToEducatorAreaWithBackButton(
      targetSpaceId,
      undefined,
      remainingFrom
    );
  }
  if (fromDestination === FROM.COMMON_ROOM) {
    return goToCommonRoomWithBackButton(targetSpaceId);
  }
  if (fromDestination === FROM.ANNOUNCEMENTS) {
    return goToAnnouncementsWithBackButton(targetSpaceId);
  }
  if (fromDestination === FROM.MESSAGES) {
    const msgId = new URLSearchParams(hash).get('msgId') || undefined;
    return goToMessagesWithBackButton(targetSpaceId, msgId);
  }
  return goToMySpaceNav(targetSpaceId);
};

export const useBackButtonParams = () => {
  const { webAppFtHeaderNavigationMode112025 } = useFlags();

  let params = null;
  if (webAppFtHeaderNavigationMode112025) {
    params = `?from=${FROM.MYSPACENAV}`;
  }
  return params;
};
